import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface UseUnsavedChangesOptions {
  hasUnsavedChanges: boolean;
  message?: string;
  title?: string;
}

export const useUnsavedChanges = ({ 
  hasUnsavedChanges, 
  message = "You have unsaved changes. Are you sure you want to navigate away?",
  title = "Unsaved Changes"
}: UseUnsavedChangesOptions) => {
  const [showWarning, setShowWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle browser back/forward and page refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    const handlePopState = (e: PopStateEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        setShowWarning(true);
        setPendingNavigation(location.pathname);
        // Push the current state back to prevent navigation
        window.history.pushState(null, '', location.pathname);
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, message, location.pathname]);

  // Custom navigation function that checks for unsaved changes
  const navigateWithWarning = useCallback((to: string) => {
    if (hasUnsavedChanges) {
      setShowWarning(true);
      setPendingNavigation(to);
    } else {
      navigate(to);
    }
  }, [hasUnsavedChanges, navigate]);

  // Handle confirmation to leave
  const handleConfirmLeave = useCallback(() => {
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
    setShowWarning(false);
    setPendingNavigation(null);
  }, [navigate, pendingNavigation]);

  // Handle cancellation
  const handleCancelLeave = useCallback(() => {
    setShowWarning(false);
    setPendingNavigation(null);
  }, []);

  return {
    showWarning,
    navigateWithWarning,
    handleConfirmLeave,
    handleCancelLeave
  };
};
